import type { BoardSummary, SpaceSummary } from '@doriavboard/shared';
import {
  ArrowLeft,
  FileUp,
  LogOut,
  MoreVertical,
  Pencil,
  SearchX,
  Share2,
  Trash2,
} from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { errorMessage } from '../api/client';
import { boardsApi, spacesApi } from '../api/endpoints';
import { BoardGrid } from '../components/BoardGrid';
import { CenteredMessage } from '../components/CenteredMessage';
import { LogoMark } from '../components/Logo';
import { NewBoardStrip, templateTitle } from '../components/NewBoardStrip';
import { SpaceShareDialog } from '../components/SpaceShareDialog';
import { UserMenu } from '../components/UserMenu';
import { Avatar } from '../components/ui/Avatar';
import { Button } from '../components/ui/Button';
import { Menu, MenuContent, MenuItem, MenuSeparator, MenuTrigger } from '../components/ui/Menu';
import { FullPageSpinner } from '../components/ui/Spinner';
import { Tip } from '../components/ui/Tooltip';
import { spacePeopleLabel } from '../lib/users';
import { confirmDialog, promptDialog } from '../stores/dialogs';
import { useSession } from '../stores/session';
import { toast } from '../stores/toasts';

/** One space: its boards, its people, and new boards made right inside it. */
export function SpacePage() {
  const { spaceId = '' } = useParams();
  const navigate = useNavigate();
  const me = useSession((s) => s.user);
  const [space, setSpace] = useState<SpaceSummary | null>(null);
  const [boards, setBoards] = useState<BoardSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [shareOpen, setShareOpen] = useState(false);
  const importInput = useRef<HTMLInputElement>(null);

  const loadSpace = useCallback(async () => {
    try {
      setSpace((await spacesApi.get(spaceId)).space);
    } catch (err) {
      setError(errorMessage(err));
    }
  }, [spaceId]);

  useEffect(() => {
    setSpace(null);
    setBoards(null);
    setError(null);
    void loadSpace();
    spacesApi
      .boards(spaceId)
      .then(({ boards: list }) => setBoards(list))
      .catch(() => setBoards([]));
  }, [spaceId, loadSpace]);

  useEffect(() => {
    if (space) document.title = `${space.name} · DoriavBoard`;
  }, [space]);

  if (error) {
    return (
      <CenteredMessage icon={<SearchX className="size-6" />} title="Space not found">
        <p>It may have been deleted, or you’re no longer in it.</p>
        <p className="mt-4">
          <Link to="/" className="font-medium text-brand-600 hover:underline">
            Back to your boards
          </Link>
        </p>
      </CenteredMessage>
    );
  }
  if (!space) return <FullPageSpinner label="Opening space…" />;

  const isOwner = space.role === 'owner';
  const canAdd = space.role !== 'viewer';

  async function rename() {
    if (!space) return;
    const name = await promptDialog({
      title: 'Rename space',
      initialValue: space.name,
      confirmLabel: 'Rename',
    });
    if (!name || name === space.name) return;
    try {
      setSpace((await spacesApi.rename(space.id, name)).space);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function remove() {
    if (!space) return;
    const ok = await confirmDialog({
      title: `Delete the space “${space.name}”?`,
      message:
        'Its boards aren’t deleted: each one goes back to the person who made it, and the people in this space lose access to them.',
      confirmLabel: 'Delete space',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await spacesApi.remove(space.id);
      toast('Space deleted');
      navigate('/');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function leave() {
    if (!space || !me) return;
    const ok = await confirmDialog({
      title: `Leave “${space.name}”?`,
      message: 'You’ll lose access to its boards unless the owner adds you again.',
      confirmLabel: 'Leave',
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await spacesApi.removeMember(space.id, me.id);
      navigate('/');
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  async function importFile(file: File) {
    if (!space) return;
    try {
      const { board } = await boardsApi.import(file, space.id);
      toast(`Imported “${board.title}”`, 'success');
      navigate(`/b/${board.id}`);
    } catch (err) {
      toast(errorMessage(err), 'error');
    }
  }

  const people = [space.owner, ...space.members];

  return (
    <div className="min-h-full">
      <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/90 backdrop-blur dark:border-slate-800 dark:bg-slate-950/90">
        <div className="mx-auto flex h-16 max-w-7xl items-center gap-3 px-4 sm:px-6">
          <Tip label="All boards">
            <Link
              to="/"
              className="focus-ring grid size-9 place-items-center rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
              aria-label="Back to boards"
            >
              <ArrowLeft className="size-5" />
            </Link>
          </Tip>
          <LogoMark className="hidden size-7 sm:block" />
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-lg font-semibold" title={space.name}>
              {space.name}
            </h1>
            <p className="truncate text-xs text-slate-500">
              {isOwner ? spacePeopleLabel(space, me?.id) : `${space.owner.name}’s space`}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setShareOpen(true)}
            className="focus-ring hidden -space-x-1.5 rounded-full sm:flex"
            aria-label="People in this space"
          >
            {people.slice(0, 5).map((person) => (
              <Avatar key={person.id} name={person.name} color={person.color} size="sm" ring />
            ))}
          </button>
          <Button variant="primary" size="sm" onClick={() => setShareOpen(true)}>
            <Share2 className="size-4" />
            Share
          </Button>
          <Menu>
            <MenuTrigger
              className="focus-ring rounded-lg p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:hover:bg-slate-800"
              aria-label="Space actions"
            >
              <MoreVertical className="size-4" />
            </MenuTrigger>
            <MenuContent>
              {canAdd && (
                <MenuItem icon={<FileUp />} onSelect={() => importInput.current?.click()}>
                  Import a board (.dvb)
                </MenuItem>
              )}
              {isOwner && (
                <MenuItem icon={<Pencil />} onSelect={rename}>
                  Rename space
                </MenuItem>
              )}
              {canAdd && <MenuSeparator />}
              {isOwner ? (
                <MenuItem icon={<Trash2 />} tone="danger" onSelect={remove}>
                  Delete space
                </MenuItem>
              ) : (
                <MenuItem icon={<LogOut />} tone="danger" onSelect={leave}>
                  Leave space
                </MenuItem>
              )}
            </MenuContent>
          </Menu>
          <input
            ref={importInput}
            type="file"
            accept=".dvb"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0];
              e.target.value = '';
              if (file) void importFile(file);
            }}
          />
          <UserMenu />
        </div>
      </header>

      {canAdd && (
        <NewBoardStrip
          heading={`Start a new board in “${space.name}”`}
          create={async (template) =>
            (await spacesApi.createBoard(space.id, { template, title: templateTitle(template) }))
              .board.id
          }
        />
      )}

      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
        <h2 className="mb-4 text-sm font-medium text-slate-600 dark:text-slate-300">
          Boards in this space
        </h2>
        <BoardGrid
          boards={boards}
          empty={
            canAdd
              ? 'No boards here yet. Start one above, or move one in from your boards with “Move to space…”.'
              : 'No boards here yet.'
          }
          onUpdate={setBoards}
          showSpace={false}
          belongs={(board) => board.space?.id === space.id}
        />
      </main>

      <SpaceShareDialog
        space={space}
        open={shareOpen}
        onOpenChange={setShareOpen}
        onChanged={() => void loadSpace()}
      />
    </div>
  );
}
